import React, { useEffect, useState, createRef } from "react"
import { graphql, Link } from "gatsby"
import lottie from "lottie-web"
import Seo from "../components/seo"
import StashImg from "../images/home/stash.svg"
import Breakdown from "../images/home/breakdown.svg"
import LoanImage from "../images/home/loans-image.png"
import AppleStore from "../images/button-app-store@2x.png"
import PlayStore from "../images/button-play-store@2x.png"
import SparklePayment from "../images/home/sparklePay.svg"
import SparkleVirtualCards from "../images/sparkle-virtual-cards.svg"
import "../commons/section.scss"

// Animations
import SparkleBannerAnimation from "../animations/banner-animation.json"
import Layout from "../components/layout"
import Nav from "../components/nav"
import Sections from "../commons/Section"
import DescriptionWrapper from "../commons/DescriptionWrapper"
import Paragraph from "../commons/Paragraph"
import AlternatingHeader from "../components/AlternatingHeader"
import { HeroSection } from "../style/styles"
import DownloadButtons from "../commons/DownloadButton"
import ParternedHomeSection from "../components/ParternedHomeSection"
import SectionWrapper from "../commons/SectionWrapper"
import CyanWave from "../components/CyanWave"
import DarkWave from "../components/DarkWave"

const IndexPage = ({ data }) => {
  let bannerAnimation = createRef()

  const [activeContent, setActiveContent] = useState(true)

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setActiveContent(!activeContent)
    }, 5000)
    return () => {
      clearTimeout(timeOut)
    }
  }, [activeContent])

  useEffect(() => {
    const anim = lottie.loadAnimation({
      container: bannerAnimation.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: SparkleBannerAnimation,
    })
    return () => anim.destroy() // optional clean up for unmounting
  })

  return (
    <Layout>
      <Seo />
      <HeroSection>
        <div className="nav-wrapper">
          <div className="container">
            <Nav />
          </div>
        </div>
        <div>
          {data.contentfulSparkleUpdate.status === "Active" && (
            <AlternatingHeader
              activeContent={activeContent}
              url={data.contentfulSparkleUpdate.url}
              update={data.contentfulSparkleUpdate.update}
              urlTextToDisplay={data.contentfulSparkleUpdate.urlTextToDisplay}
            />
          )}
        </div>

        <div className="container">
          <div className="row align-center pb-3">
            <div className="col-lg-6 my-5 my-lg-5 pb-5 py-lg-5">
              <div className="description--wrapper">
                <h1 className="">We are not just a bank, we're a tribe...</h1>
                <p>
                  Explore an easier and better way to save, make payments,
                  manage your money whenever you want, wherever you are!
                </p>
              </div>
              <DownloadButtons
                appleIcon={AppleStore}
                appleLink={data.site.siteMetadata.appStoreLink}
                playstoreIcon={PlayStore}
                playStoreLink={data.site.siteMetadata.playStoreLink}
              />

              <p data-aos="fade-up" data-aos-duration="2000">
                Sparkle Microfinance Bank is licensed by the{" "}
                <span>Central Bank of Nigeria (CBN).</span> All deposits are
                insured by the{" "}
                <span>Nigerian Deposit Insurance Corporation (NDIC)</span>.
              </p>
            </div>
            <div className="col-lg-6 order-first order-lg-last">
              <div
                className="annimation-container sparkle--animation pt-lg-5"
                ref={bannerAnimation}
              />
            </div>
          </div>
        </div>
        <DarkWave />
      </HeroSection>

      <ParternedHomeSection />
      <CyanWave />
      <SectionWrapper sectionStyles="section-wrapper">
        <Sections
          img={StashImg}
          imageTitle="Open a Sparkle Account in minutes & start saving"
          sectionTitle="Account Opening & Savings"
          title="Account Opening & Savings"
          reRouteLink="features/stash"
        >
          <DescriptionWrapper>
            <Paragraph>
              Open a Sparkle Account with no documents or bank queues. All can
              be done on your smartphone in under 5 minutes.
            </Paragraph>
            <Paragraph>
              You can save money for rainy days using our Sparkle Stash feature
              which allows you save as you like, periodically, with investments,
              and with a group of people
            </Paragraph>
          </DescriptionWrapper>
        </Sections>

        <Sections
          img={Breakdown}
          imageTitle="Analyse your expenses and categorise your finances"
          sectionTitle="Breakdown & Analytics"
          title="Analyse your expenses and categorise your finances"
          reRouteLink="/features/breakdown"
          orderStyle="change-order"
        >
          <DescriptionWrapper>
            <Paragraph>
              Keep track of what you spend, when you spend and how you spend.
              Get the daily, weekly and monthly breakdown of your expenses and
              find out which habit you feed the most.
            </Paragraph>
            <Paragraph>
              You get a month in view of all your finances showing various tags
              and categories of what was spent in the previous month.
            </Paragraph>
          </DescriptionWrapper>
        </Sections>
      </SectionWrapper>
      <CyanWave />
      <SectionWrapper sectionStyles="section-wrapper">
        <Sections
          img={SparklePayment}
          imageTitle="Send & Request Money and  Bills Payments made easy"
          sectionTitle="Transfers & Bills payments"
          title="Send & Request Money and Bills Payments made easy"
          reRouteLink="/features/payment"
        >
          <DescriptionWrapper>
            <Paragraph>
              Send, request money, or split bills with friends and family
              instantly, whether they are sparklers or from other Nigerian
              banks.
            </Paragraph>
            <Paragraph>
              Also top up mobile airtime, data, cable, utility & transport
              bills. Other exciting payment features like scheduled payments, QR
              payment & <b>SparklePay</b> shows innovative payments like never
              before.
            </Paragraph>
          </DescriptionWrapper>
        </Sections>
        <Sections
          sectionTitle="SPARKLE CARDS"
          title="Enjoy Seamless Payments with your Sparkle Debit Card"
          img={SparkleVirtualCards}
          imageTitle="Go Physical with your Sparkle Cards"
          reRouteLink="/features/sparkle-cards"
          orderStyle="change-order"
        >
          <Paragraph>
            Make secure and easy purchases online and in-person with Sparkle
            physical card. Have your physical card delivered right to you
            anywhere you are in Lagos, Nigeria.
          </Paragraph>
          <Paragraph>
            You have card controls on your sparkle card that block or freezes
            your card in case of emergencies & card limits to manage your daily,
            weekly or monthly expenses on your card.
          </Paragraph>
        </Sections>{" "}
        <div className="container">
          <div className="row align-center py-lg-2 my-lg-2">
            <div className="col-lg-5 position-relative">
              <img
                loading="lazy"
                src={LoanImage}
                className="img-fluid position-relative"
                alt="Send & Request Money and  Bills Payments made easy"
                title="Send & Request Money and  Bills Payments made easy"
                data-aos="fade-up"
                data-aos-duration="2000"
              />
            </div>

            <div className="col-lg-6 pt-lg-0 pt-5">
              <p
                className="text-uppercase sparkle--mini sparkle-green"
                data-aos="fade-up"
                data-aos-duration="2000"
              >
                Sparkle Loans
              </p>
              <h4
                className="sparkle--title"
                data-aos="fade-up"
                data-aos-duration="2000"
              >
                Quick cash to help you do what you want to do.
              </h4>

              <div className="description--wrapper">
                <p
                  className="sparkle--paragraph"
                  data-aos="fade-up"
                  data-aos-duration="2000"
                >
                  No more waiting around. Get the quick cash you need to do what
                  you want to do, when you want to do it. Apply for a loan and
                  get the money in a snap. No paperwork, collateral or guarantor
                  needed!
                </p>
              </div>

              <p
                className="sparkle--link mt-5"
                data-aos="fade-up"
                data-aos-duration="2000"
              >
                <Link to="features/sparkle-loan">
                  Learn more{" "}
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.55176 1.58203C5.27832 1.85547 5.30566 2.26562 5.55176 2.53906L8.86035 5.65625H1.0127C0.629883 5.65625 0.356445 5.95703 0.356445 6.3125V7.1875C0.356445 7.57031 0.629883 7.84375 1.0127 7.84375H8.86035L5.55176 10.9883C5.30566 11.2617 5.30566 11.6719 5.55176 11.9453L6.15332 12.5469C6.42676 12.793 6.83691 12.793 7.08301 12.5469L12.415 7.21484C12.6611 6.96875 12.6611 6.55859 12.415 6.28516L7.08301 0.980469C6.83691 0.734375 6.42676 0.734375 6.15332 0.980469L5.55176 1.58203Z"
                      fill="#46B839"
                    />
                  </svg>
                </Link>
              </p>
            </div>
          </div>
        </div>
      </SectionWrapper>
    </Layout>
  )
}

export default IndexPage

export const query = graphql`
  {
    site {
      siteMetadata {
        appStoreLink
        playStoreLink
      }
    }
    SparkleTribe: file(relativePath: { eq: "sparkle-hero-img.png" }) {
      childImageSharp {
        gatsbyImageData(width: 500, layout: CONSTRAINED)
      }
    }
    SparkleFreedom: file(relativePath: { eq: "sparkle-freedom.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH)
      }
    }
    SparkleStash: file(relativePath: { eq: "sparkle-stash.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: TRACED_SVG, layout: FULL_WIDTH)
      }
    }
    SparkleExpenseBreakdown: file(
      relativePath: { eq: "sparkle-expense-breakdown.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: TRACED_SVG, layout: FULL_WIDTH)
      }
    }
    contentfulSparkleUpdate {
      status
      update
      url
      urlTextToDisplay
    }
  }
`
